import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { BLOG_POSTS } from '../mock';
import { TRANSCRIPTS } from '../transcripts';
import { Calendar, ArrowLeft, ArrowRight, Share2, Facebook, Twitter, Linkedin, FileText, ChevronDown } from 'lucide-react';
import CTASection from '../components/CTASection';
import useSEO from '../hooks/useSEO';

const inline = (t) =>
  t
    .replace(/\*\*(.+?)\*\*/g, '<strong class="text-white font-semibold">$1</strong>')
    .replace(/\*(.+?)\*/g, '<em class="italic">$1</em>')
    .replace(/_(.+?)_/g, '<em class="italic text-white/70">$1</em>')
    .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" class="text-[#F5CD1B] underline hover:text-white" target="_blank" rel="noopener noreferrer">$1</a>');

const safe = (raw) => ({ __html: DOMPurify.sanitize(inline(raw), { ALLOWED_TAGS: ['strong', 'em', 'a', 'br'], ALLOWED_ATTR: ['href', 'class', 'target', 'rel'] }) });

const MarkdownListItem = ({ text }) => <li dangerouslySetInnerHTML={safe(text)} />;
const MarkdownParagraph = ({ text }) => (
  <p className="text-white/80 leading-[1.85] my-5 text-lg" dangerouslySetInnerHTML={safe(text)} />
);

const renderMarkdown = (md) => {
  if (!md) return null;
  const lines = md.split('\n');
  const out = [];
  let listBuf = [];
  let ordered = false;

  const flushList = () => {
    if (listBuf.length) {
      const Tag = ordered ? 'ol' : 'ul';
      const items = listBuf;
      out.push(
        <Tag key={`list-${out.length}`} className={`${ordered ? 'list-decimal' : 'list-disc'} pl-6 space-y-2 my-5 text-white/80`}>
          {items.map((item, i) => (
            <MarkdownListItem key={`item-${out.length}-${i}-${item.slice(0, 20)}`} text={item} />
          ))}
        </Tag>
      );
      listBuf = [];
    }
  };

  lines.forEach((raw, idx) => {
    const line = raw.trimEnd();
    if (!line.trim()) { flushList(); return; }

    if (line.startsWith('## ')) {
      flushList();
      out.push(<h2 key={`h2-${idx}`} className="font-display text-3xl md:text-4xl font-bold text-white mt-12 mb-5">{line.slice(3)}</h2>);
    } else if (line.startsWith('### ')) {
      flushList();
      out.push(<h3 key={`h3-${idx}`} className="font-display text-2xl font-bold text-[#F5CD1B] mt-10 mb-4">{line.slice(4)}</h3>);
    } else if (line.startsWith('---')) {
      flushList();
      out.push(<hr key={`hr-${idx}`} className="border-white/10 my-10" />);
    } else if (/^\d+\.\s/.test(line)) {
      if (!ordered) flushList();
      ordered = true;
      listBuf.push(line.replace(/^\d+\.\s/, ''));
    } else if (/^[-*]\s/.test(line)) {
      if (ordered) flushList();
      ordered = false;
      listBuf.push(line.replace(/^[-*]\s/, ''));
    } else {
      flushList();
      out.push(<MarkdownParagraph key={`p-${idx}`} text={line} />);
    }
  });
  flushList();
  return out;
};

const BlogPost = () => {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((p) => p.slug === slug);
  const [showTranscript, setShowTranscript] = useState(false);

  useSEO({
    title: post ? `${post.title}, Marketing Agency Liverpool | Yellow` : 'Yellow Blog, Marketing Agency Liverpool',
    description: post ? (post.excerpt || post.description || 'Practical marketing advice from Yellow, marketing agency Liverpool.').slice(0, 158) : undefined,
    path: `/marketing-agency-liverpool/${slug || ''}`,
    image: post?.cover
  });

  const resetOnSlugChange = useCallback(() => {
    window.scrollTo(0, 0);
    setShowTranscript(false);
  }, []);

  useEffect(() => { resetOnSlugChange(); }, [slug, resetOnSlugChange]);

  const body = useMemo(() => renderMarkdown(post?.body), [post?.body]);

  if (!post) return <Navigate to="/blog" />;

  const transcript = TRANSCRIPTS[slug];

  const others = BLOG_POSTS.filter((p) => p.slug !== slug).slice(0, 3);
  const idx = BLOG_POSTS.findIndex((p) => p.slug === slug);
  const prev = idx > 0 ? BLOG_POSTS[idx - 1] : null;
  const next = idx < BLOG_POSTS.length - 1 ? BLOG_POSTS[idx + 1] : null;

  return (
    <>
      {/* Header */}
      <section className="pt-36 pb-12 lg:pt-44">
        <div className="max-w-[900px] mx-auto px-6">
          <Link to="/blog" className="inline-flex items-center gap-2 text-white/60 hover:text-[#F5CD1B] text-sm mb-8 transition-colors">
            <ArrowLeft size={16} /> Back to blog
          </Link>
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <span className="text-[10px] uppercase tracking-widest text-[#F5CD1B] bg-[#F5CD1B]/10 border border-[#F5CD1B]/25 px-3 py-1.5 rounded-md">{post.category}</span>
            <span className="flex items-center gap-1.5 text-white/50 text-xs"><Calendar size={12} /> {post.date}</span>
          </div>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-black text-white leading-[1.06]">
            {post.title}
          </h1>
          <p className="text-white/70 text-xl leading-relaxed mt-8">{post.excerpt}</p>
        </div>
      </section>

      {/* Feature image or video */}
      <section className="pb-12">
        <div className="max-w-[1100px] mx-auto px-6">
          {post.videoId ? (
            <div className="rounded-2xl overflow-hidden aspect-video bg-black border border-white/10">
              <iframe
                className="w-full h-full"
                src={`https://www.youtube.com/embed/${post.videoId}?rel=0&modestbranding=1`}
                title={post.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : (
            <div className="rounded-2xl overflow-hidden aspect-[16/8] bg-[#111114] border border-white/10">
              <img src={post.image} alt={post.title} className="w-full h-full object-cover" onError={(e)=>{e.target.style.display='none'}} />
            </div>
          )}
        </div>
      </section>

      {/* Body */}
      <article className="pb-20">
        <div className="max-w-[820px] mx-auto px-6">
          {post.body ? (
            <div className="prose-yellow">
              {body}
            </div>
          ) : (
            <div>
              <p className="text-white/80 leading-[1.85] my-5 text-lg">{post.excerpt}</p>
              <p className="text-white/70 leading-[1.85] my-5">
                Want more insights like this? Explore our <Link to="/blog" className="text-[#F5CD1B] underline hover:text-white">full blog</Link> or <Link to="/contact" className="text-[#F5CD1B] underline hover:text-white">get in touch</Link> for a personalised marketing chat.
              </p>
            </div>
          )}

          {/* Transcript */}
          {transcript && (
            <div className="mt-12 border border-white/10 rounded-2xl bg-[#111114] overflow-hidden">
              <button
                onClick={() => setShowTranscript(!showTranscript)}
                className="w-full flex items-center justify-between p-6 hover:bg-white/[0.02] transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#F5CD1B]/10 border border-[#F5CD1B]/25 flex items-center justify-center">
                    <FileText size={16} className="text-[#F5CD1B]" />
                  </div>
                  <div className="text-left">
                    <p className="section-label mb-0.5">Video transcript</p>
                    <p className="text-white font-semibold text-sm">Read the full conversation</p>
                  </div>
                </div>
                <ChevronDown size={18} className={`text-white/60 transition-transform ${showTranscript ? 'rotate-180' : ''}`} />
              </button>
              {showTranscript && (
                <div className="px-6 pb-8 pt-2 border-t border-white/5">
                  {transcript.split('\n\n').map((p, i) => (
                    <p key={i} className="text-white/75 leading-[1.85] my-4 text-[15px]">{p}</p>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Share */}
          <div className="mt-14 pt-8 border-t border-white/10 flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <Share2 size={16} className="text-white/50" />
              <span className="text-white/60 text-sm uppercase tracking-widest">Share</span>
              <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center hover:border-[#F5CD1B] hover:text-[#F5CD1B]"><Facebook size={14} /></a>
              <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(post.title)}`} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center hover:border-[#F5CD1B] hover:text-[#F5CD1B]"><Twitter size={14} /></a>
              <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center hover:border-[#F5CD1B] hover:text-[#F5CD1B]"><Linkedin size={14} /></a>
            </div>
            <Link to="/contact" className="btn-yellow inline-flex items-center gap-2 px-5 py-3 rounded-md uppercase text-xs">
              Get a quote <ArrowRight size={14} />
            </Link>
          </div>

          {/* Prev / Next */}
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
            {prev && (
              <Link to={prev.path} className="group bg-[#111114] border border-white/10 rounded-2xl p-6 hover:border-[#F5CD1B]/30 transition-colors">
                <p className="section-label mb-2 flex items-center gap-2"><ArrowLeft size={12} /> Previous</p>
                <p className="font-display text-lg font-bold text-white group-hover:text-[#F5CD1B] transition-colors">{prev.title}</p>
              </Link>
            )}
            {next && (
              <Link to={next.path} className="group bg-[#111114] border border-white/10 rounded-2xl p-6 hover:border-[#F5CD1B]/30 transition-colors md:text-right">
                <p className="section-label mb-2 flex items-center gap-2 md:justify-end">Next <ArrowRight size={12} /></p>
                <p className="font-display text-lg font-bold text-white group-hover:text-[#F5CD1B] transition-colors">{next.title}</p>
              </Link>
            )}
          </div>
        </div>
      </article>

      {/* Related */}
      <section className="py-20 bg-[#08080a]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <p className="section-label mb-4">Similar posts</p>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-black text-white mb-10">Keep reading.</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {others.map((p) => (
              <Link key={p.id} to={p.path} className="card-hover group bg-[#111114] border border-white/10 rounded-2xl overflow-hidden">
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" onError={(e)=>{e.target.style.display='none'}} />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-[10px] uppercase tracking-widest text-[#F5CD1B]">{p.category}</span>
                    <span className="flex items-center gap-1 text-white/50 text-xs"><Calendar size={12} /> {p.date}</span>
                  </div>
                  <h3 className="font-display text-lg font-bold text-white group-hover:text-[#F5CD1B] transition-colors leading-snug">{p.title}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
};

export default BlogPost;
